import React, { useState, useMemo } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Clock, Video, Phone, Plus, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get user's timezone
const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Generate next 10 business days
const getNextBusinessDays = (count = 10) => {
  const days = [];
  let current = new Date();
  current.setHours(0, 0, 0, 0);
  
  while (days.length < count) {
    current.setDate(current.getDate() + 1);
    const dayOfWeek = current.getDay();
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      days.push(new Date(current));
    }
  }
  return days;
};

// Generate time slots for a day (9:00 - 18:00)
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 9; hour <= 17; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

const ScheduleInterviewModal = ({ 
  isOpen, 
  onClose, 
  resource, 
  company,
  onSuccess 
}) => {
  const [step, setStep] = useState(1); // 1: Select slots, 2: Details
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [duration, setDuration] = useState('30');
  const [meetingPlatforms, setMeetingPlatforms] = useState(['Google Meet']);
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const timezone = getUserTimezone();
  const businessDays = useMemo(() => getNextBusinessDays(10), []);
  const timeSlots = useMemo(() => generateTimeSlots(), []);

  const meetingOptions = [
    { value: 'Google Meet', label: 'Google Meet', icon: Video },
    { value: 'Microsoft Teams', label: 'Microsoft Teams', icon: Video },
    { value: 'Zoom', label: 'Zoom', icon: Video },
    { value: 'Phone', label: 'Phone Call', icon: Phone },
    { value: 'WhatsApp', label: 'WhatsApp', icon: Phone },
    { value: 'Telegram', label: 'Telegram', icon: Phone },
  ];

  const durationOptions = [
    { value: '15', label: '15 min' },
    { value: '30', label: '30 min' },
    { value: '45', label: '45 min' },
    { value: '60', label: '60 min' },
  ];

  const toggleSlot = (date, time) => {
    const slotKey = `${date.toISOString().split('T')[0]}_${time}`;
    setSelectedSlots(prev => {
      if (prev.includes(slotKey)) {
        return prev.filter(s => s !== slotKey);
      }
      if (prev.length >= 10) {
        toast.error('Maximum 10 slots can be selected');
        return prev;
      }
      return [...prev, slotKey];
    });
  };

  const isSlotSelected = (date, time) => {
    const slotKey = `${date.toISOString().split('T')[0]}_${time}`;
    return selectedSlots.includes(slotKey);
  };

  const togglePlatform = (platform) => {
    setMeetingPlatforms(prev => {
      if (prev.includes(platform)) {
        if (prev.length === 1) {
          toast.error('At least one platform must be selected');
          return prev;
        }
        return prev.filter(p => p !== platform);
      }
      return [...prev, platform];
    });
  };

  const handleSubmit = async () => {
    if (selectedSlots.length === 0) {
      toast.error('Please select at least one time slot');
      return;
    }

    setSending(true);
    const token = localStorage.getItem('token');

    try {
      // Parse additional emails
      const emailList = additionalEmails
        .split(/[,;\s]+/)
        .map(e => e.trim())
        .filter(e => e && e.includes('@'));

      const interviewData = {
        resource_id: resource.id,
        resource_company_id: company?.id || resource.company_id,
        proposed_slots: selectedSlots.map(slot => {
          const [date, time] = slot.split('_');
          return {
            date,
            time,
            timezone
          };
        }),
        duration_minutes: parseInt(duration),
        preferred_platforms: meetingPlatforms,
        additional_invitees: emailList,
        message: message.trim(),
        timezone
      };

      await axios.post(`${API}/interviews/request`, interviewData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Interview request sent successfully!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to send interview request:', error);
      toast.error(error.response?.data?.detail || 'Failed to send interview request');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Schedule Interview with {resource?.first_name} {resource?.last_name}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-6">
            {/* Timezone notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <Clock className="w-4 h-4 inline mr-1" />
                All times shown in your timezone: <strong>{timezone}</strong>
              </p>
            </div>

            {/* Calendar Grid */}
            <div className="space-y-4">
              <Label className="text-lg font-semibold">
                Select your available time slots (next 10 business days)
              </Label>
              <p className="text-sm text-gray-500">
                Select multiple slots to give the Resource Manager flexibility. Maximum 10 slots.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 border bg-gray-50 sticky left-0 z-10">Time</th>
                      {businessDays.map((day, idx) => (
                        <th key={idx} className="p-2 border bg-gray-50 min-w-[80px] text-xs">
                          {formatDate(day)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {timeSlots.map((time) => (
                      <tr key={time}>
                        <td className="p-2 border bg-gray-50 font-medium text-sm sticky left-0 z-10">
                          {time}
                        </td>
                        {businessDays.map((day, idx) => {
                          const selected = isSlotSelected(day, time);
                          return (
                            <td 
                              key={idx} 
                              className="p-1 border cursor-pointer hover:bg-blue-50 transition-colors"
                              onClick={() => toggleSlot(day, time)}
                            >
                              <div className={`w-full h-8 rounded flex items-center justify-center transition-all ${
                                selected 
                                  ? 'bg-green-500 text-white' 
                                  : 'bg-gray-100 hover:bg-blue-100'
                              }`}>
                                {selected && <Check className="w-4 h-4" />}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Selected slots summary */}
              {selectedSlots.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-green-800 mb-2">
                    {selectedSlots.length} slot(s) selected:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {selectedSlots.map(slot => {
                      const [date, time] = slot.split('_');
                      return (
                        <span 
                          key={slot}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full flex items-center gap-1"
                        >
                          {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {time}
                          <button 
                            onClick={(e) => { e.stopPropagation(); toggleSlot(new Date(date), time); }}
                            className="hover:text-red-600"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={selectedSlots.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Continue
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Duration */}
            <div>
              <Label className="text-sm font-medium">Interview Duration</Label>
              <div className="flex gap-2 mt-2">
                {durationOptions.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setDuration(opt.value)}
                    className={`px-4 py-2 rounded-lg border transition-all ${
                      duration === opt.value
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white border-gray-300 hover:border-blue-400'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Meeting Platform */}
            <div>
              <Label className="text-sm font-medium">Preferred Meeting Platform(s)</Label>
              <p className="text-xs text-gray-500 mb-2">Select one or more options</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {meetingOptions.map(opt => {
                  const Icon = opt.icon;
                  const selected = meetingPlatforms.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      onClick={() => togglePlatform(opt.value)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all ${
                        selected
                          ? 'bg-blue-50 border-blue-400 text-blue-700'
                          : 'bg-white border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {opt.label}
                      {selected && <Check className="w-4 h-4 ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Additional Invitees */}
            <div>
              <Label className="text-sm font-medium">Additional Invitees (optional)</Label>
              <p className="text-xs text-gray-500 mb-2">Add email addresses of other people to invite, separated by commas</p>
              <Input
                placeholder="colleague@company.com, manager@company.com"
                value={additionalEmails}
                onChange={(e) => setAdditionalEmails(e.target.value)}
              />
            </div>

            {/* Message */}
            <div>
              <Label className="text-sm font-medium">Additional Message (optional)</Label>
              <Textarea
                placeholder="Any specific topics you'd like to discuss, or information for the resource manager..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">Interview Request Summary</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><strong>Resource:</strong> {resource?.first_name} {resource?.last_name}</p>
                <p><strong>Duration:</strong> {duration} minutes</p>
                <p><strong>Platforms:</strong> {meetingPlatforms.join(', ')}</p>
                <p><strong>Proposed Slots:</strong> {selectedSlots.length}</p>
                <p><strong>Timezone:</strong> {timezone}</p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={sending}
                className="bg-green-600 hover:bg-green-700"
              >
                {sending ? 'Sending...' : 'Send Interview Request'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleInterviewModal;
